/**
 * react-sweet-state singleton store for Rovo Spaces. Mutating actions keep the
 * list and detail slices in sync so all consumers see a consistent view.
 */

import { createStore, type Action, type StoreActionApi, type Store } from 'react-sweet-state';

import {
	createSpaceMemoryFetchAction,
	type CreateSpaceMemoryFetchParams,
} from '../../services/useCreateSpaceMemory/createSpaceMemoryFetch';
import { deleteSpaceMemoryFetchAction } from '../../services/useDeleteSpaceMemory/deleteSpaceMemoryFetch';
import {
	getSpaceFetchAction,
	type GetSpaceFetchParams,
} from '../../services/useGetSpace/useGetSpace.fetch';
import {
	getSpaceConversationsFetchAction,
	type GetSpaceConversationsFetchParams,
} from '../../services/useGetSpaceConversations/useGetSpaceConversations.fetch';
import {
	getSpaceMemoryFetchAction,
	type GetSpaceMemoryFetchParams,
} from '../../services/useGetSpaceMemory/useGetSpaceMemory.fetch';
import { listSpacesFetchAction } from '../../services/useListSpaces/useListSpaces.fetch';
import {
	updateSpaceFetchAction,
	type UpdateSpaceFetchParams,
} from '../../services/useUpdateSpace/useUpdateSpace.fetch';

import type {
	CreateSpaceResponse,
	UpdateSpaceResponse,
	Space,
	SpaceConversationsResponse,
	SpaceMemoryItem,
	SpaceResponse,
	SpaceSourceResponse,
	SpaceStoreState,
	SmartContextLinkRecommendation,
} from './types';

// --- Initial state ---

/** Derives a stable flat deduplicated conversationIds array from a spaces list. */
const computeConversationIds = (spaces: Space[]): string[] => {
	const ids = new Set<string>();
	for (const space of spaces) {
		if (!space.sources) continue;
		for (const source of space.sources) {
			if (!source.conversationIds) continue;
			for (const id of source.conversationIds) ids.add(id);
		}
	}
	return Array.from(ids);
};

/** Fresh initial state on every call so `reset()` doesn't share Set/Map refs. */
const makeInitialState = (): SpaceStoreState => ({
	spaces: [],
	conversationIds: [],
	isListLoading: false,
	listError: null,
	hasFetchedList: false,
	deletedIds: new Set(),
	pendingAdds: new Map(),
	lastFetchedListParams: null,
	spaceDetails: new Map(),
	detailLoading: new Map(),
	detailErrors: new Map(),
	spaceConversations: new Map(),
	conversationsLoading: new Map(),
	conversationsErrors: new Map(),
	spaceMemories: new Map(),
	memoriesLoading: new Map(),
	memoriesErrors: new Map(),
	smartContextLinkRecommendations: new Map(),
});

/** Project a {@link SpaceResponse} down to the lighter {@link Space} list shape. */
const detailToListShape = (response: SpaceResponse): Partial<Space> => ({
	name: response.name,
	emoji: response.emoji ?? null,
	description: response.description ?? null,
	owner: response.owner,
	createdAt: response.createdAt,
	updatedAt: response.updatedAt,
});

/** Build a `Space` from a `CreateSpaceResponse`, defaulting fields not yet present. */
const createResponseToSpace = (response: CreateSpaceResponse, emojiId?: string): Space => ({
	id: response.id,
	name: response.name,
	owner: response.owner,
	description: response.description ?? null,
	emoji: emojiId ?? null,
	threadId: null,
	createdAt: response.createdAt,
	updatedAt: response.updatedAt,
	conversationPage: null,
});

/** Immutable Map setter — returns a new Map so sweet-state equality checks fire. */
const mapSet = <K, V>(map: Map<K, V>, key: K, value: V): Map<K, V> => {
	const next = new Map(map);
	next.set(key, value);
	return next;
};

const mapDelete = <K, V>(map: Map<K, V>, key: K): Map<K, V> => {
	if (!map.has(key)) {
		return map;
	}
	const next = new Map(map);
	next.delete(key);
	return next;
};

const replaceOrAppend = <T>(arr: T[], item: T, getKey: (it: T) => string): T[] => {
	const key = getKey(item);
	const idx = arr.findIndex((it) => getKey(it) === key);
	if (idx < 0) {
		return [...arr, item];
	}
	const next = arr.slice();
	next[idx] = item;
	return next;
};

// --- Actions ---

const actions = {
	/** Test-only — restores the store to its initial empty state. */
	reset:
		(): Action<SpaceStoreState> =>
		({ setState }: StoreActionApi<SpaceStoreState>) => {
			setState(makeInitialState());
		},

	/**
	 * Merge spaces into the store (applies tombstones, re-injects pending adds).
	 * `{ isLoading: true }` = loading snapshot; `{ error }` = record fetch failure.
	 */
	setSpaces:
		(
			nextSpaces: Space[],
			{
				isLoading: nextIsLoading = false,
				error: nextError = null,
			}: { isLoading?: boolean; error?: Error | null } = {},
		): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			const { deletedIds, pendingAdds, hasFetchedList } = getState();

			// 1. Drop any rows that have been optimistically deleted client-side.
			const withoutDeleted =
				deletedIds.size === 0 ? nextSpaces : nextSpaces.filter((s) => !deletedIds.has(s.id));

			// 2. Reconcile pending adds. On a real server response, drop pending
			//    entries that the server now knows about; on a loading snapshot,
			//    leave them untouched.
			let merged = withoutDeleted;
			let nextPendingAdds = pendingAdds;
			if (pendingAdds.size > 0) {
				const serverIds = new Set(withoutDeleted.map((s) => s.id));
				const stillPending: Space[] = [];
				const isServerResponse = !nextIsLoading && nextError === null;
				if (isServerResponse) {
					nextPendingAdds = new Map(pendingAdds);
				}
				pendingAdds.forEach((space, id) => {
					if (serverIds.has(id)) {
						if (isServerResponse) {
							nextPendingAdds.delete(id);
						}
					} else {
						stillPending.push(space);
					}
				});
				if (stillPending.length > 0) {
					merged = [...withoutDeleted, ...stillPending];
				}
			}

			setState({
				spaces: merged,
				conversationIds: computeConversationIds(merged),
				isListLoading: nextIsLoading,
				listError: nextError,
				hasFetchedList: !nextIsLoading && nextError === null ? true : hasFetchedList,
				pendingAdds: nextPendingAdds,
			});
		},

	addSpace:
		(response: CreateSpaceResponse, emojiId?: string): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			const space = createResponseToSpace(response, emojiId);
			const { spaces, deletedIds, pendingAdds } = getState();
			// If a space with this id was previously tombstoned, clear it so the
			// new space (potentially reusing an id) is allowed to surface again.
			const nextDeletedIds = deletedIds.has(response.id)
				? new Set(Array.from(deletedIds).filter((id) => id !== response.id))
				: deletedIds;
			// Idempotent guard: if a space with this id is already in the list
			// (e.g. because another `addSpace` call has already run for the same
			// `CreateSpaceModal.onCreated` event, or a server list-fetch landed
			// before us), DO NOT append a duplicate row. We still want to
			// (a) clear any tombstone (handled above) and
			// (b) keep the entry in `pendingAdds` so a subsequent `setSpaces`
			//     loading-snapshot can't drop it before the server confirms it.
			if (spaces.some((s) => s.id === response.id)) {
				if (deletedIds.has(response.id) || !pendingAdds.has(response.id)) {
					setState({
						deletedIds: nextDeletedIds,
						pendingAdds: mapSet(pendingAdds, response.id, space),
					});
				}
				return;
			}
			const nextPendingAdds = mapSet(pendingAdds, response.id, space);
			const nextSpacesAdd = [...spaces, space];
			setState({
				spaces: nextSpacesAdd,
				conversationIds: computeConversationIds(nextSpacesAdd),
				deletedIds: nextDeletedIds,
				pendingAdds: nextPendingAdds,
			});
		},

	removeSpace:
		(spaceId: string): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			const { spaces, deletedIds, pendingAdds } = getState();
			const nextDeletedIds = new Set(deletedIds);
			nextDeletedIds.add(spaceId);
			const nextPendingAdds = mapDelete(pendingAdds, spaceId);
			const nextSpacesRemove = spaces.filter((s) => s.id !== spaceId);
			setState({
				spaces: nextSpacesRemove,
				conversationIds: computeConversationIds(nextSpacesRemove),
				deletedIds: nextDeletedIds,
				pendingAdds: nextPendingAdds,
			});
		},

	restoreSpace:
		(space: Space): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			const { spaces, deletedIds } = getState();
			const nextDeletedIds = new Set(deletedIds);
			nextDeletedIds.delete(space.id);
			const nextSpacesRestore = spaces.some((s) => s.id === space.id) ? spaces : [...spaces, space];
			setState({
				spaces: nextSpacesRestore,
				conversationIds: computeConversationIds(nextSpacesRestore),
				deletedIds: nextDeletedIds,
			});
		},

	patchSpace:
		(spaceId: string, patch: Partial<Space>): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			const { spaces, spaceDetails } = getState();
			// Mirror the patch into the detail slice if a detail entry exists, so
			// any open ManageSpaceModal stays consistent with the side-nav list.
			let nextDetails = spaceDetails;
			const existing = spaceDetails.get(spaceId);
			if (existing) {
				// Only mirror fields that exist on the detail shape. The list-only
				// `sources` field intentionally has a different element type
				// (`SpaceSource` vs `SpaceSourceResponse`) and is owned by the
				// dedicated `upsertSpaceSource` / `removeSpaceSource` actions.
				const {
					sources: _ignoredSources,
					conversationPage: _ignoredCP,
					threadId: _ignoredTid,
					...detailPatch
				} = patch;
				nextDetails = mapSet(spaceDetails, spaceId, { ...existing, ...detailPatch });
			}
			const nextSpacesPatch = spaces.map((s) => (s.id === spaceId ? { ...s, ...patch } : s));
			setState({
				spaces: nextSpacesPatch,
				conversationIds: computeConversationIds(nextSpacesPatch),
				spaceDetails: nextDetails,
			});
		},

	/**
	 * Fetches the list of spaces from the BE and merges into the store. Marks
	 * `isListLoading=true` *without* clearing the existing list so any
	 * optimistic state is preserved while the request is in flight.
	 *
	 * Idempotent: if the store has already fetched for the same params, this
	 * is a no-op unless `force=true`.
	 */
	fetchSpaces:
		(params: {
			cloudId: string;
			product: string;
			experienceId: string;
			force?: boolean;
		}): Action<SpaceStoreState, void, Promise<void>> =>
		async ({ getState, setState, dispatch }: StoreActionApi<SpaceStoreState>): Promise<void> => {
			const { cloudId, product, experienceId, force = false } = params;
			const key = `${cloudId}|${product}|${experienceId}`;
			const { hasFetchedList, lastFetchedListParams, spaces } = getState();
			if (!force && hasFetchedList && lastFetchedListParams === key) {
				return;
			}
			// Loading snapshot — keep current spaces visible while fetching.
			dispatch(actions.setSpaces(spaces, { isLoading: true }));
			try {
				const response = await listSpacesFetchAction({
					cloudId,
					product,
					experienceId,
				});
				dispatch(actions.setSpaces(response.data));
				setState({ lastFetchedListParams: key });
			} catch (err) {
				const error = err instanceof Error ? err : new Error(String(err));
				dispatch(actions.setSpaces(getState().spaces, { error }));
			}
		},

	setSpaceDetail:
		(spaceId: string, response: SpaceResponse): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			const { spaces, spaceDetails, detailLoading, detailErrors } = getState();
			setState({
				spaceDetails: mapSet(spaceDetails, spaceId, response),
				detailLoading: mapSet(detailLoading, spaceId, false),
				detailErrors: mapSet(detailErrors, spaceId, null),
				// Keep list-shape in sync if this space is in the list.
				spaces: spaces.some((s) => s.id === spaceId)
					? spaces.map((s) => (s.id === spaceId ? { ...s, ...detailToListShape(response) } : s))
					: spaces,
			});
		},

	setSpaceDetailLoading:
		(spaceId: string, isLoading: boolean): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			setState({
				detailLoading: mapSet(getState().detailLoading, spaceId, isLoading),
			});
		},

	setSpaceDetailError:
		(spaceId: string, error: Error | null): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			setState({
				detailErrors: mapSet(getState().detailErrors, spaceId, error),
				detailLoading: mapSet(getState().detailLoading, spaceId, false),
			});
		},

	/**
	 * Fetches a single space by id and populates the detail slice. Always
	 * fetches when called (consumers wanting cache-only reads should use
	 * `useSpaceById`).
	 */
	fetchSpace:
		(params: GetSpaceFetchParams): Action<SpaceStoreState, void, Promise<void>> =>
		async ({ dispatch }: StoreActionApi<SpaceStoreState>): Promise<void> => {
			const { spaceId } = params;
			dispatch(actions.setSpaceDetailLoading(spaceId, true));
			dispatch(actions.setSpaceDetailError(spaceId, null));
			try {
				const response = await getSpaceFetchAction(params);
				dispatch(actions.setSpaceDetail(spaceId, response));
			} catch (err) {
				const error = err instanceof Error ? err : new Error(String(err));
				dispatch(actions.setSpaceDetailError(spaceId, error));
			}
		},

	/**
	 * Performs a PUT against the spaces API and, on success, patches both the
	 * list and detail slices so all consumers see the change immediately.
	 */
	updateSpace:
		(params: UpdateSpaceFetchParams): Action<SpaceStoreState, void, Promise<UpdateSpaceResponse>> =>
		async ({ dispatch }: StoreActionApi<SpaceStoreState>) => {
			const response = await updateSpaceFetchAction(params);
			// Patch both slices via patchSpace which knows to update both.
			dispatch(
				actions.patchSpace(params.spaceId, {
					name: response.name,
					emoji: response.emoji ?? null,
					description: response.description ?? null,
					updatedAt: response.updatedAt,
				}),
			);
			return response;
		},

	/**
	 * Patches a source into the detail slice's `sources` array (and the list
	 * slice's `sources` array if present). Used by `addSpaceSource` after a
	 * successful POST to keep the open ManageSpaceModal in sync.
	 */
	upsertSpaceSource:
		(spaceId: string, source: SpaceSourceResponse): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			const { spaces, spaceDetails } = getState();
			const detail = spaceDetails.get(spaceId);
			const nextDetails =
				detail !== undefined
					? mapSet(spaceDetails, spaceId, {
							...detail,
							sources: replaceOrAppend(detail.sources ?? [], source, (s) => s.id),
						})
					: spaceDetails;
			const nextSpaces = spaces.map((s) =>
				s.id === spaceId
					? {
							...s,
							sources: replaceOrAppend(
								s.sources ?? [],
								{
									id: source.id,
									ari: source.ari,
									url: source.url,
									sourceType: source.sourceType,
									name: source.name ?? null,
								},
								(it) => it.id,
							),
						}
					: s,
			);
			setState({
				spaces: nextSpaces,
				conversationIds: computeConversationIds(nextSpaces),
				spaceDetails: nextDetails,
			});
		},

	removeSpaceSource:
		(spaceId: string, sourceId: string): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			const { spaces, spaceDetails } = getState();
			const detail = spaceDetails.get(spaceId);
			const nextDetails =
				detail !== undefined
					? mapSet(spaceDetails, spaceId, {
							...detail,
							sources: (detail.sources ?? []).filter((s) => s.id !== sourceId),
						})
					: spaceDetails;
			const nextSpaces = spaces.map((s) =>
				s.id === spaceId
					? { ...s, sources: (s.sources ?? []).filter((it) => it.id !== sourceId) }
					: s,
			);
			setState({
				spaces: nextSpaces,
				conversationIds: computeConversationIds(nextSpaces),
				spaceDetails: nextDetails,
			});
		},

	setSpaceConversations:
		(
			spaceId: string,
			page: SpaceConversationsResponse | null,
			isLoading: boolean,
			error: Error | null,
		): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			const { spaceConversations, conversationsLoading, conversationsErrors } = getState();
			setState({
				spaceConversations:
					page !== null ? mapSet(spaceConversations, spaceId, page) : spaceConversations,
				conversationsLoading: mapSet(conversationsLoading, spaceId, isLoading),
				conversationsErrors: mapSet(conversationsErrors, spaceId, error),
			});
		},

	fetchSpaceConversations:
		(params: GetSpaceConversationsFetchParams): Action<SpaceStoreState, void, Promise<void>> =>
		async ({ dispatch }: StoreActionApi<SpaceStoreState>) => {
			const { spaceId } = params;
			dispatch(actions.setSpaceConversations(spaceId, null, true, null));
			try {
				const response = await getSpaceConversationsFetchAction(params);
				dispatch(actions.setSpaceConversations(spaceId, response, false, null));
			} catch (err) {
				const error = err instanceof Error ? err : new Error(String(err));
				dispatch(actions.setSpaceConversations(spaceId, null, false, error));
			}
		},

	/** Internal setter — drives loading/error state and populates the memory list. */
	setSpaceMemories:
		(
			spaceId: string,
			items: SpaceMemoryItem[] | null,
			isLoading: boolean,
			error: Error | null,
		): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			const { spaceMemories, memoriesLoading, memoriesErrors } = getState();
			setState({
				spaceMemories: items !== null ? mapSet(spaceMemories, spaceId, items) : spaceMemories,
				memoriesLoading: mapSet(memoriesLoading, spaceId, isLoading),
				memoriesErrors: mapSet(memoriesErrors, spaceId, error),
			});
		},

	/**
	 * Lazily fetches the memory list for a space from GET /spaces/{spaceId}/memory.
	 * Only fires a network request the first time it's called for a given spaceId
	 * (or if a previous fetch errored).
	 */
	fetchSpaceMemories:
		(params: GetSpaceMemoryFetchParams): Action<SpaceStoreState, void, Promise<void>> =>
		async ({ getState, dispatch }: StoreActionApi<SpaceStoreState>) => {
			const { spaceId } = params;
			const { memoriesLoading, spaceMemories } = getState();
			// Skip if already loading or already fetched successfully.
			if (memoriesLoading.get(spaceId) || spaceMemories.has(spaceId)) {
				return;
			}
			dispatch(actions.setSpaceMemories(spaceId, null, true, null));
			try {
				const response = await getSpaceMemoryFetchAction(params);
				const items: SpaceMemoryItem[] = response.items.map((item) => ({
					id: item.id,
					text: item.content,
				}));
				dispatch(actions.setSpaceMemories(spaceId, items, false, null));
			} catch (err) {
				const error = err instanceof Error ? err : new Error(String(err));
				dispatch(actions.setSpaceMemories(spaceId, null, false, error));
			}
		},

	/**
	 * POSTs a new memory item and optimistically inserts it at the end of the
	 * list so the UI updates immediately without a refetch.
	 */
	createSpaceMemory:
		(params: CreateSpaceMemoryFetchParams): Action<SpaceStoreState, void, Promise<void>> =>
		async ({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			const response = await createSpaceMemoryFetchAction(params);
			const { spaceMemories } = getState();
			const { spaceId } = params;
			const existing = spaceMemories.get(spaceId) ?? [];
			setState({
				spaceMemories: mapSet(spaceMemories, spaceId, [
					...existing,
					{ id: response.id, text: response.content },
				]),
			});
		},

	/**
	 * Optimistically removes a memory item from the store, then fires the
	 * DELETE request. Rolls back on failure.
	 */
	removeSpaceMemory:
		(
			spaceId: string,
			memoryId: string,
			cloudId: string,
			product: string,
			experienceId: string,
		): Action<SpaceStoreState, void, Promise<void>> =>
		async ({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			const { spaceMemories } = getState();
			const existing = spaceMemories.get(spaceId) ?? [];
			const optimistic = existing.filter((m) => m.id !== memoryId);
			setState({ spaceMemories: mapSet(spaceMemories, spaceId, optimistic) });
			try {
				await deleteSpaceMemoryFetchAction({ spaceId, memoryId, cloudId, product, experienceId });
			} catch (err) {
				// Roll back the optimistic removal.
				setState({ spaceMemories: mapSet(getState().spaceMemories, spaceId, existing) });
				throw err;
			}
		},

	setSmartContextLinkRecommendations:
		(spaceId: string, recommendations: SmartContextLinkRecommendation[]): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			setState({
				smartContextLinkRecommendations: mapSet(
					getState().smartContextLinkRecommendations,
					spaceId,
					recommendations,
				),
			});
		},

	clearSmartContextLinkRecommendations:
		(spaceId: string): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			setState({
				smartContextLinkRecommendations: mapDelete(
					getState().smartContextLinkRecommendations,
					spaceId,
				),
			});
		},

	removeSmartContextLinkRecommendation:
		(spaceId: string, recommendationId: string): Action<SpaceStoreState> =>
		({ getState, setState }: StoreActionApi<SpaceStoreState>) => {
			const { smartContextLinkRecommendations } = getState();
			const existing = smartContextLinkRecommendations.get(spaceId);
			if (!existing) {
				return;
			}
			setState({
				smartContextLinkRecommendations: mapSet(
					smartContextLinkRecommendations,
					spaceId,
					existing.filter((recommendation) => recommendation.recommendationId !== recommendationId),
				),
			});
		},
};

// --- Store ---

export const spaceStore: Store<SpaceStoreState, typeof actions> = createStore({
	name: 'spaceStore',
	initialState: makeInitialState(),
	actions,
});

export type SpaceStoreActions = typeof actions;
